'use client'

import Link from 'next/link'
import { formatDistanceToNow } from 'date-fns'
import {
  BarChart2,
  DoorOpen,
  Eye,
  Moon,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PeriodChangeCaption } from '@/components/period-change-caption'
import { cn } from '@/lib/utils'

/** Rooms with no customer activity for this many days are considered quiet. */
export const QUIET_ROOM_DAYS = 14

/** Only rooms with activity inside this window can be "most active". */
export const MOST_ACTIVE_WINDOW_DAYS = 30

export type DashboardUsageStats = {
  activeUsers: number
  activeUsersChange: number
  contentViews: number
  contentViewsChange: number
  roomVisits?: number
  roomVisitsChange?: number
}

export type DashboardUsageRoom = {
  id: string
  name: string
  customerActivity: number
  lastActivity: string | null
  status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' | string | null
}

type DashboardUsageInsightsProps = {
  stats: DashboardUsageStats | null
  isLoadingStats: boolean
  rooms: DashboardUsageRoom[]
  className?: string
}

function daysSince(timestamp: string | null): number | null {
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
}

function isQuietRoom(room: DashboardUsageRoom): boolean {
  if (room.status === 'DRAFT' || room.status === 'ARCHIVED') return false
  if (room.customerActivity === 0 || !room.lastActivity) return true
  const days = daysSince(room.lastActivity)
  return days !== null && days >= QUIET_ROOM_DAYS
}

function isRecentlyActive(room: DashboardUsageRoom): boolean {
  if (room.status === 'DRAFT' || room.status === 'ARCHIVED') return false
  if (room.customerActivity <= 0 || !room.lastActivity) return false
  const days = daysSince(room.lastActivity)
  return days !== null && days < MOST_ACTIVE_WINDOW_DAYS
}

function pickMostActiveRoom(rooms: DashboardUsageRoom[]): DashboardUsageRoom | undefined {
  return [...rooms]
    .filter(isRecentlyActive)
    .sort((a, b) => {
      if (b.customerActivity !== a.customerActivity) {
        return b.customerActivity - a.customerActivity
      }
      return (daysSince(a.lastActivity) ?? Infinity) - (daysSince(b.lastActivity) ?? Infinity)
    })[0]
}

export function DashboardUsageInsights({
  stats,
  isLoadingStats,
  rooms,
  className,
}: DashboardUsageInsightsProps) {
  const quietRooms = rooms
    .filter(isQuietRoom)
    .sort((a, b) => {
      const aDays = daysSince(a.lastActivity) ?? Number.POSITIVE_INFINITY
      const bDays = daysSince(b.lastActivity) ?? Number.POSITIVE_INFINITY
      return bDays - aDays
    })
    .slice(0, 3)

  const topRoom = pickMostActiveRoom(rooms)

  const metrics = [
    {
      label: 'Room visits',
      value: stats?.roomVisits ?? 0,
      change: stats?.roomVisitsChange ?? 0,
      icon: DoorOpen,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Content views',
      value: stats?.contentViews ?? 0,
      change: stats?.contentViewsChange ?? 0,
      icon: Eye,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    {
      label: 'Unique visitors',
      value: stats?.activeUsers ?? 0,
      change: stats?.activeUsersChange ?? 0,
      icon: Users,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-600',
    },
  ]

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-6 mt-8', className)}>
      <Card className="bg-white border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-blue-500" />
            Room activity
            <span className="text-xs font-normal text-slate-400 ml-1">Last 30 days</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-5">
            {metrics.map((metric) => {
              const Icon = metric.icon
              return (
                <div
                  key={metric.label}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn('p-2.5 rounded-xl', metric.iconBg)}>
                      <Icon className={cn('w-4 h-4', metric.iconColor)} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-600">{metric.label}</p>
                      {!isLoadingStats && stats && (
                        <div className="mt-1">
                          <PeriodChangeCaption change={metric.change} pill />
                        </div>
                      )}
                    </div>
                  </div>
                  {isLoadingStats ? (
                    <Skeleton className="h-8 w-14" />
                  ) : (
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">
                      {metric.value.toLocaleString()}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 rounded-2xl">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Needs attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {quietRooms.length > 0 ? (
              <li>
                <div className="flex items-start space-x-4 p-3 rounded-xl bg-slate-50/80">
                  <div className="p-2.5 rounded-xl border bg-amber-50 text-amber-600 border-amber-100 flex items-center justify-center shadow-sm">
                    <Moon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-900">
                      {quietRooms.length === 1
                        ? '1 quiet room'
                        : `${quietRooms.length} quiet rooms`}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 mb-2">
                      No customer visits in {QUIET_ROOM_DAYS}+ days
                    </p>
                    <ul className="space-y-1.5">
                      {quietRooms.map((room) => (
                        <li key={room.id}>
                          <Link
                            href={`/room/${room.id}`}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 truncate block"
                          >
                            {room.name}
                            <span className="text-slate-400 font-normal">
                              {' '}
                              ·{' '}
                              {room.lastActivity
                                ? formatDistanceToNow(new Date(room.lastActivity), {
                                    addSuffix: true,
                                  })
                                : 'never visited'}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            ) : (
              <li className="flex items-start space-x-4 p-3 rounded-xl">
                <div className="p-2.5 rounded-xl border bg-emerald-50 text-emerald-600 border-emerald-100 flex items-center justify-center shadow-sm">
                  <Moon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900">No quiet rooms</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    All active rooms had a visit within the last {QUIET_ROOM_DAYS} days
                  </p>
                </div>
              </li>
            )}

            {topRoom && (
              <li>
                <Link
                  href={`/room/${topRoom.id}/analytics`}
                  className="flex items-start space-x-4 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
                >
                  <div className="p-2.5 rounded-xl border bg-emerald-50 text-emerald-600 border-emerald-100 flex items-center justify-center shadow-sm">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <p className="font-semibold text-sm text-slate-900 truncate">
                        Most active room
                      </p>
                      <span className="text-xs font-medium text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        View →
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      &ldquo;{topRoom.name}&rdquo; ·{' '}
                      {topRoom.customerActivity.toLocaleString()} activities · last visit{' '}
                      {topRoom.lastActivity
                        ? formatDistanceToNow(new Date(topRoom.lastActivity), {
                            addSuffix: true,
                          })
                        : 'unknown'}
                    </p>
                  </div>
                </Link>
              </li>
            )}

            {!topRoom && quietRooms.length === 0 && (
              <li className="flex items-start space-x-4 p-3 rounded-xl">
                <div className="p-2.5 rounded-xl border bg-slate-50 text-slate-500 border-slate-100 flex items-center justify-center shadow-sm">
                  <Users className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900">Waiting for visitors</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Share a room to start seeing customer visits and content views here
                  </p>
                </div>
              </li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
